import { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import { useFetchPlanes, useCreatePlan, useUpdatePlan, useDeletePlan } from '@/shared/actions/erp/comisiones/comisiones.actions';
import type { PlanComision } from '@/lib/commissions/types';
import { PLANES_PREDEFINIDOS } from '@/lib/commissions/constants';
import LoadingDialog from './LoadingDialog';

interface PlanesFormProps {
  onSaved: () => void;
}

export default function PlanesForm({ onSaved }: PlanesFormProps) {
  const { data: planes, isLoading } = useFetchPlanes();
  const createMutation = useCreatePlan({ enableSuccessAlert: true });
  const updateMutation = useUpdatePlan({ enableSuccessAlert: true });
  const deleteMutation = useDeletePlan();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Partial<PlanComision> | null>(null);
  const [loadingDialogOpen, setLoadingDialogOpen] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const handleOpenDialog = (plan?: PlanComision) => {
    if (plan) {
      setEditingPlan(plan);
    } else {
      setEditingPlan({
        code: '',
        nombre: '',
        precio_unitario: 0,
        activo: true,
        normalizado: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPlan(null);
  };

  const handleSave = async () => {
    if (!editingPlan) return;

    try {
      if (editingPlan.id) {
        await updateMutation.mutateAsync(editingPlan as PlanComision);
      } else {
        await createMutation.mutateAsync(editingPlan);
      }
      onSaved();
      handleCloseDialog();
    } catch (error) {
      console.error('Error al guardar plan:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (confirm('¿Estás seguro de eliminar este plan?')) {
      await deleteMutation.mutateAsync(id);
      onSaved();
    }
  };

  const handleRestablecer = async () => {
    if (!confirm('¿Estás seguro de restablecer todos los planes a los valores predefinidos? Esto eliminará todos los planes existentes y creará nuevos con los valores predeterminados.')) {
      return;
    }

    try {
      setLoadingDialogOpen(true);
      setLoadingProgress(0);

      const totalSteps = (planes?.length || 0) + PLANES_PREDEFINIDOS.length;
      let currentStep = 0;

      // Eliminar todos los planes existentes
      if (planes && planes.length > 0) {
        for (const plan of planes) {
          if (plan.id) {
            await deleteMutation.mutateAsync(plan.id);
            currentStep++;
            setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
          }
        }
      }

      // Crear los planes predefinidos
      for (const plan of PLANES_PREDEFINIDOS) {
        await createMutation.mutateAsync({
          code: plan.code,
          nombre: plan.nombre,
          precio_unitario: plan.precio_unitario,
          activo: plan.activo,
          normalizado: plan.code,
        });
        currentStep++;
        setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
      }

      onSaved();
      setLoadingDialogOpen(false);
      setLoadingProgress(0);
    } catch (error) {
      console.error('Error al restablecer planes:', error);
      setLoadingDialogOpen(false);
      setLoadingProgress(0);
    }
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Planes de Comisión</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={handleRestablecer}
            size="small"
            color="warning"
            disabled={createMutation.isPending || deleteMutation.isPending}
          >
            Restablecer
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nuevo Plan
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell align="right">Precio Unitario</TableCell>
              <TableCell>Activo</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {planes?.map((plan) => (
              <TableRow key={plan.id}>
                <TableCell>{plan.code}</TableCell>
                <TableCell>{plan.nombre}</TableCell>
                <TableCell align="right">${Number(plan.precio_unitario).toFixed(2)}</TableCell>
                <TableCell>
                  <Chip
                    label={plan.activo ? 'Activo' : 'Inactivo'}
                    size="small"
                    color={plan.activo ? 'success' : 'default'}
                    sx={{
                      bgcolor: plan.activo ? 'success.main' : 'grey.400',
                      color: 'white',
                      fontWeight: 500,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(plan)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => plan.id && handleDelete(plan.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de edición */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPlan?.id ? 'Editar Plan' : 'Nuevo Plan'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Código del Plan"
              value={editingPlan?.code || ''}
              onChange={(e) => {
                const newCode = e.target.value.toUpperCase();
                setEditingPlan({
                  ...editingPlan,
                  code: newCode,
                  normalizado: newCode
                });
              }}
              fullWidth
              required
              helperText="Código único del plan (ej. SPORT1000, FULL, GOLD)"
              placeholder="SPORT1000"
            />
            <TextField
              label="Nombre del Plan"
              value={editingPlan?.nombre || ''}
              onChange={(e) => setEditingPlan({ ...editingPlan, nombre: e.target.value })}
              fullWidth
              required
              helperText="Este nombre tiene que ser igual al del plan registrado en el sistema Softnet"
              placeholder="HOGAR SPORTS 1000MBPS"
            />
            <TextField
              label="Precio Unitario"
              type="number"
              value={editingPlan?.precio_unitario || 0}
              onChange={(e) => setEditingPlan({ ...editingPlan, precio_unitario: parseFloat(e.target.value) })}
              fullWidth
              required
              inputProps={{ step: 0.01, min: 0 }}
              helperText="Precio de venta del plan (ej. 24.99)"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">Estado del plan:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color={editingPlan?.activo ? 'success.main' : 'text.secondary'}>
                  {editingPlan?.activo ? 'Activo' : 'Inactivo'}
                </Typography>
                <Switch
                  checked={editingPlan?.activo || false}
                  onChange={(e) => setEditingPlan({ ...editingPlan, activo: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'success.main',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'success.main',
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de carga */}
      <LoadingDialog
        open={loadingDialogOpen}
        progress={loadingProgress}
        title="Restableciendo planes..."
        message="Eliminando planes actuales y creando los valores predefinidos."
      />
    </Box>
  );
}
